import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { format, parseISO } from 'date-fns';

/**
 * Confirmation dialog shown when the backend reports DUPLICATE_JOB_EXISTS
 * (HTTP 409) on /batch-schedule/job or /batch-schedule/batch/:id/add-job.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void                         — close without proceeding
 *  - onConfirm: () => void                       — proceed (caller resubmits with confirm_duplicate=true)
 *  - duplicateInfo: {                            — payload from server 409 detail
 *      site_name: string,
 *      scheduled_date: 'YYYY-MM-DD',
 *      existing_jobs: [{
 *        id, scheduled_date, end_date, status,
 *        client_reference, batch_status,
 *        auditor_names: string[]
 *      }]
 *    }
 *  - confirmLabel: optional custom button label (default: "Add anyway")
 *
 * iter431 — duplicate-job prevention. Surfaces the conflict cleanly so
 * the scheduler can see WHO already has the job before clicking through.
 */
export const DuplicateJobConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  duplicateInfo,
  confirmLabel = 'Add anyway',
}) => {
  if (!duplicateInfo) return null;

  const existing = duplicateInfo.existing_jobs || [];
  const dateLabel = (() => {
    try {
      return format(parseISO(duplicateInfo.scheduled_date), 'EEEE d MMMM yyyy');
    } catch {
      return duplicateInfo.scheduled_date;
    }
  })();

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-[520px]" data-testid="duplicate-job-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="h-5 w-5" />
            Duplicate job warning
          </DialogTitle>
          <DialogDescription className="pt-1">
            <span className="font-medium">{duplicateInfo.site_name}</span> already has
            {existing.length === 1 ? ' a job ' : ` ${existing.length} jobs `}
            scheduled for <span className="font-medium">{dateLabel}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
          {existing.map((j) => (
            <div
              key={j.id}
              className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm"
              data-testid={`duplicate-job-row-${j.id}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-800">
                  Job #{j.id}
                  {j.client_reference ? ` · ${j.client_reference}` : ''}
                </span>
                <span className="text-xs text-slate-600 uppercase tracking-wide">
                  {j.batch_status === 'DRAFT' ? 'in draft batch'
                    : j.batch_status === 'PENDING_APPROVAL' ? 'pending approval'
                    : j.batch_status === 'RETURNED' ? 'returned to scheduler'
                    : 'approved'}
                </span>
              </div>
              <div className="mt-1 text-slate-600">
                Status: <span className="font-medium">{j.status}</span>
                {j.auditor_names && j.auditor_names.length > 0 && (
                  <span> · Auditor{j.auditor_names.length > 1 ? 's' : ''}: {j.auditor_names.join(', ')}</span>
                )}
                {j.end_date && j.end_date !== j.scheduled_date && (
                  <span> · until {j.end_date}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="duplicate-job-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={onConfirm}
            data-testid="duplicate-job-confirm-btn"
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateJobConfirmDialog;
